import { EdgeInsets, Point, Rectangle } from '@printu/geom';
import { generateFancyArrowPoints } from 'app/core/graphics/Arrow';
export const getItemAbsolutePoints = (item) => [
    new Point(item.x, item.y),
    new Point(item.x + item.endX, item.y + item.endY),
];
export const getItemAbsoluteVertices = (item) => {
    if (!item) {
        return null;
    }
    const [start, end] = getItemAbsolutePoints(item);
    let xMin = 0;
    let yMin = 0;
    let xMax = 0;
    let yMax = 0;
    if (start.x < end.x) {
        xMin = start.x;
        xMax = end.x;
    }
    else {
        xMin = end.x;
        xMax = start.x;
    }
    if (start.y < end.y) {
        yMin = start.y;
        yMax = end.y;
    }
    else {
        yMin = end.y;
        yMax = start.y;
    }
    return [
        new Point(xMin, yMin),
        new Point(xMax, yMin),
        new Point(xMax, yMax),
        new Point(xMin, yMax),
    ];
};
export const getItemAbsoluteBox = (item) => {
    if (!item) {
        return null;
    }
    const [tl, , br] = getItemAbsoluteVertices(item);
    return new Rectangle(tl.x, tl.y, br.x - tl.x, br.y - tl.y);
};
export const getItemBoxInterpolation = (marker) => {
    if (!marker) {
        return null;
    }
    switch (marker.alias) {
        case 'Line': {
            let bounds;
            const line = marker;
            const sizex2 = line.size * 2;
            const [start, end] = getItemAbsolutePoints(line);
            if (Point.isCollinear(start, end)) {
                bounds = new Rectangle();
                bounds.topLeft = new Point(Math.min(start.x, end.x), Math.min(start.y, end.y));
                bounds.bottomRight = new Point(Math.max(start.x, end.x), Math.max(start.y, end.y));
                const insets = new EdgeInsets(sizex2);
                bounds.inflateEdgeInsets(insets);
            }
            else {
                bounds = getItemAbsoluteBox(line);
                if (bounds.width < sizex2) {
                    bounds.inflate(sizex2, 0);
                }
                if (bounds.height < sizex2) {
                    bounds.inflate(0, sizex2);
                }
            }
            return [bounds];
        }
        case 'Polyline': {
            const { bbox } = marker;
            if (!bbox) {
                return [new Rectangle(marker.x, marker.y, 10, 10)];
            }
            return [
                new Rectangle(marker.x + bbox.x, marker.y + bbox.y, bbox.width, bbox.height),
            ];
        }
        case 'Arrow': {
            const arrow = marker;
            const [anchor, ...points] = generateFancyArrowPoints(new Point(arrow.x, arrow.y), new Point(arrow.x + arrow.endX, arrow.y + arrow.endY), 1.5 * arrow.size);
            const bounds = new Rectangle(anchor.x, anchor.y, 0, 0);
            points.forEach(point => {
                const { x, y } = point;
                if (x < bounds.left) {
                    bounds.left = x;
                }
                else if (x > bounds.right) {
                    bounds.right = x;
                }
                if (y < bounds.top) {
                    bounds.top = y;
                }
                else if (y > bounds.bottom) {
                    bounds.bottom = y;
                }
            });
            return [bounds];
        }
        case 'Path':
        case 'Highlight': {
            const { bbox } = marker;
            if (!bbox) {
                return [new Rectangle()];
            }
            return [
                new Rectangle(marker.x + bbox.x, marker.y + bbox.y, bbox.width, bbox.height),
            ];
        }
        case 'Zoom': {
            const zoomMarker = marker;
            const [, end] = getItemAbsolutePoints(zoomMarker);
            const { width, height } = zoomMarker;
            // const radius2 = radius * 2;
            const widthHalf = width * 0.5;
            const heightHalf = height * 0.5;
            return [
                getItemAbsoluteBox(marker),
                // new Rectangle(end.x - radius, end.y - radius, radius2, radius2),
                new Rectangle(end.x - widthHalf, end.y - heightHalf, width, height),
            ];
        }
        case 'Pin': {
            const pinMarker = marker;
            const { x, y, width, height } = pinMarker;
            const radius = Math.max(width, height);
            const radius2 = 2 * radius;
            return [new Rectangle(x - radius, y - radius, radius2, radius2)];
        }
        case 'Text': {
            const textMarker = marker;
            const { lines } = textMarker;
            if (!lines.length) {
                return [
                    new Rectangle(marker.x, marker.y, textMarker.width, textMarker.height),
                ];
            }
            const offsetX = marker.x;
            const offsetY = marker.y - lines[0].ascent;
            const lineBounds = lines.map(line => new Rectangle(offsetX + line.x, offsetY + line.y - line.ascent, line.width, line.height));
            return lineBounds;
        }
        case 'Image': {
            const imageMarker = marker;
            const bounds = getItemAbsoluteBox(imageMarker);
            // if (imageMarker.crop) {
            //     const cropBounds = applyCrop(bounds, imageMarker.crop);
            //     return [
            //         new Rectangle(
            //             bounds.x,
            //             bounds.y,
            //             cropBounds.width,
            //             cropBounds.height
            //         ),
            //     ];
            // }
            return [Rectangle.from(bounds)];
        }
        default:
            return [getItemAbsoluteBox(marker)];
    }
};
export const getItemBounds = (marker) => {
    if (!marker) {
        return undefined;
    }
    switch (marker.alias) {
        case 'Text': {
            const textMarker = marker;
            const { width, height, lines } = textMarker;
            return new Rectangle(marker.x, marker.y - lines[0].ascent, width, height);
        }
        default: {
            const boxes = getItemBoxInterpolation(marker);
            let result = boxes[0].clone();
            boxes.slice(1).forEach(box => {
                result = result.union(box);
            });
            return result;
        }
    }
};
